import { extendZodWithOpenApi } from "zod-openapi";

import { z } from "zod";
extendZodWithOpenApi(z);

import {
  BlockBaseSchema,
  BlockIdPlaceholderSchema,
  ComponentBlockStyleSchema,
  FilterSchema,
} from "block-system/types/base";
import { blockType } from "./constants";
import { strings } from "./strings";
import { RecordOrderSchema } from "server/schemas/table-records";
import { TableIdSchema } from "@/server/schemas/table-tables";
import {
  ALLOWED_CHECKLIST_COMPLETED_FIELD_TYPES,
  ALLOWED_CHECKLIST_DUE_DATE_FIELD_TYPES,
  ALLOWED_CHECKLIST_LABEL_FIELD_TYPES,
  ALLOWED_CHECKLIST_TITLE_FIELD_TYPES,
} from "@/block-system/blocks/Checklist/Block/constants-and-types";
import { TABLE_FIELD_TYPE_KEY_TO_HUMAN_READABLE_NAME } from "@/server/schemas/table-fields";
import type { APIFieldType } from "@/server/services/tables/types/fields";

export const ChecklistBlockSchema = BlockBaseSchema.extend({
  type: z.literal(blockType),
  config: z.object({
    id: z.string().cuid().or(BlockIdPlaceholderSchema).optional(),

    tableId: TableIdSchema.optional().describe(
      "The ID of the Zapier Table connected to the block."
    ),

    title: z.string().default(strings.DEFAULT_CHECKLIST_TITLE_COPY),

    newListItemButtonCopy: z
      .string()
      .default(strings.NEW_LIST_ITEM_BUTTON_DEFAULT_COPY),

    listItemLabelFieldId: z
      .number()
      .optional()
      .describe(
        `The id of the field to use for the Checklist Item Label. Allowed field types are: ${formatFieldTypes(
          {
            fieldTypes: ALLOWED_CHECKLIST_LABEL_FIELD_TYPES,
          }
        )}`
      ),
    listItemCompletedFieldId: z
      .number()
      .optional()
      .describe(
        `The id of the field to use for the Checklist Item Completed. Allowed field types are: ${formatFieldTypes(
          {
            fieldTypes: ALLOWED_CHECKLIST_COMPLETED_FIELD_TYPES,
          }
        )}`
      ),
    listItemTitleFieldId: z
      .number()
      .optional()
      .describe(
        `The id of the field to use for the Checklist Item Title. Allowed field types are: ${formatFieldTypes(
          {
            fieldTypes: ALLOWED_CHECKLIST_TITLE_FIELD_TYPES,
          }
        )}`
      ),
    listItemDueDateFieldId: z
      .number()
      .optional()
      .describe(
        `The id of the field to use for the Checklist Item Due Date. Allowed field types are: ${formatFieldTypes(
          {
            fieldTypes: ALLOWED_CHECKLIST_DUE_DATE_FIELD_TYPES,
          }
        )}`
      ),

    listItemDetailFields: z.array(z.number()).default([]),

    filters: z.array(FilterSchema).default([]),

    orders: z.array(RecordOrderSchema).default([]),

    canCreateListItems: z.boolean().default(false),

    canEditListItems: z.boolean().default(true),

    canDeleteListItems: z.boolean().default(false),

    canSearchListItems: z.boolean().default(false),

    canViewDetails: z.boolean().default(true),

    canViewCompleted: z.boolean().default(true),
    /**
     * Problem: I want to provide the defaults and ensure TypeScript marks the `style` fields as non-optional.
     * Attempted: I tried to use `.default` on the schema, but that did not remove the optionality of the fields.
     * Solution: I used `.transform` & `.default`.
     */
    style: ComponentBlockStyleSchema.default({
      width: "wide",
      alignment: "center",
    }),
  }),
}).openapi({ ref: "ChecklistBlock" });

function formatFieldTypes({ fieldTypes }: { fieldTypes: APIFieldType[] }) {
  return fieldTypes
    .map((fieldType) => {
      return `${TABLE_FIELD_TYPE_KEY_TO_HUMAN_READABLE_NAME[fieldType]} (${fieldType})`;
    })
    .join(", ");
}
